import type { CommonWageListRow } from '@/types/commonWage';

/** 3년 소급 청구 가정 */
export const RETROSPECTIVE_YEARS = 3;

/** 연차수당 영향 — 25인 기준 연 150만 → 1인 6만 (PPT 고정값) */
export const ANNUAL_LEAVE_PER_PERSON = 60000;

export interface OrdinaryWageRiskInput {
  basic: number;
  meal: number;
  position: number;
  overtimePay: number;
  nightPay: number;
}

export interface OrdinaryWageRiskBreakdown {
  usualBefore: number;
  usualAfter: number;
  premiumPay: number;
  ratio: number;
  monthlyRisk: number;
  annualOvertimeNight: number;
  annualSeverance: number;
  annualLeave: number;
  risk3Year: number;
}

/** 1년 추정 위험액 (연장·야간 + 퇴직금 + 연차) */
export const calcAnnualOrdinaryWageRisk = (breakdown: OrdinaryWageRiskBreakdown): number =>
  breakdown.annualOvertimeNight + breakdown.annualSeverance + breakdown.annualLeave;

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

export const calcOrdinaryWageRiskPerPerson = (
  input: OrdinaryWageRiskInput,
): OrdinaryWageRiskBreakdown => {
  const usualBefore = input.basic;
  const usualAfter = input.basic + input.meal + input.position;
  const premiumPay = input.overtimePay + input.nightPay;

  if (usualBefore <= 0 || usualAfter <= usualBefore || premiumPay <= 0) {
    return {
      usualBefore,
      usualAfter,
      premiumPay,
      ratio: usualBefore > 0 ? usualAfter / usualBefore : 1,
      monthlyRisk: 0,
      annualOvertimeNight: 0,
      annualSeverance: 0,
      annualLeave: 0,
      risk3Year: 0,
    };
  }

  const ratio = usualAfter / usualBefore;
  const monthlyRisk = Math.round(premiumPay * (ratio - 1));
  const annualOvertimeNight = monthlyRisk * 12;
  const annualSeverance = monthlyRisk;
  const annualLeave = ANNUAL_LEAVE_PER_PERSON;
  const risk3Year = (annualOvertimeNight + annualSeverance + annualLeave) * RETROSPECTIVE_YEARS;

  return {
    usualBefore,
    usualAfter,
    premiumPay,
    ratio,
    monthlyRisk,
    annualOvertimeNight,
    annualSeverance,
    annualLeave,
    risk3Year,
  };
};

/** 요양시설 월별 임금 행 → 통상임금 위험액 (직책수당: 선임수당 필드 사용) */
export const calcOrdinaryWageRiskFromFacilityWage = (
  wage: CommonWageListRow,
): OrdinaryWageRiskBreakdown => calcOrdinaryWageRiskPerPerson({
  basic: nc(wage.cwhd_basic),
  meal: nc(wage.cwhd_lunch),
  position: nc(wage.cwhd_sunim),
  overtimePay: nc(wage.cwhd_overtime),
  nightPay: nc(wage.cwhd_night),
});
